import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Chip
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import BuildIcon from '@mui/icons-material/Build';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import BookIcon from '@mui/icons-material/Book';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import QuizIcon from '@mui/icons-material/Quiz';

// Admin components
import ClubsAdmin from '../components/admin/ClubsAdmin';
import LeaguesAdmin from '../components/admin/LeaguesAdmin';
import PlayersAdmin from '../components/admin/PlayersAdmin';
import GamesAdmin from '../components/admin/GamesAdmin';
import GameResultsAdmin from '../components/admin/GameResultsAdmin';
import NewsAdmin from '../components/admin/NewsAdmin';
import GameScraper from '../components/admin/GameScraper';
import MaterialsAdmin from '../components/admin/MaterialsAdmin';
import SeatingAdmin from '../components/admin/SeatingAdmin';
import ExportData from '../components/admin/ExportData';
import LiveGamesAdmin from '../components/admin/LiveGamesAdmin';
import QuizzesAdmin from '../components/admin/QuizzesAdmin';

// Auth and Admin permissions
import { useAuth } from '../contexts/AuthContext';
import { AdminRoleProvider, useAdminRole } from '../contexts/AdminRoleContext';

const DRAWER_WIDTH = 240;

// Menu sections and items
const MENU_ITEMS = {
  management: [
    { id: 'clubs', label: 'Clubs', icon: <BusinessIcon />, component: ClubsAdmin },
    { id: 'leagues', label: 'Leagues', icon: <EmojiEventsIcon />, component: LeaguesAdmin },
    { id: 'players', label: 'Players', icon: <PeopleIcon />, component: PlayersAdmin },
    { id: 'games', label: 'Games', icon: <SportsEsportsIcon />, component: GamesAdmin },
    { id: 'liveGames', label: 'Live Games', icon: <LiveTvIcon />, component: LiveGamesAdmin }
  ],
  tools: [
    { id: 'gameScraper', label: 'Game Scraper', icon: <BuildIcon />, component: GameScraper },
    { id: 'seating', label: 'Seating', icon: <EventSeatIcon />, component: SeatingAdmin },
    { id: 'exportData', label: 'Export Data', icon: <FileDownloadIcon />, component: ExportData }
  ],
  misc: [
    { id: 'news', label: 'News', icon: <NewspaperIcon />, component: NewsAdmin },
    { id: 'materials', label: 'Study Materials', icon: <BookIcon />, component: MaterialsAdmin },
    { id: 'quizzes', label: 'Quizzes', icon: <QuizIcon />, component: QuizzesAdmin }
  ]
};

// Convert old tab indices to new section IDs
const TAB_INDEX_TO_SECTION_ID: Record<number, string> = {
  0: 'clubs',
  1: 'leagues',
  2: 'players',
  3: 'games',
  4: 'games', // Redirect gameResults tab to games
  5: 'liveGames',
  6: 'news',
  7: 'gameScraper',
  8: 'materials',
  9: 'seating',
  10: 'exportData'
};

interface SectionPanelProps {
  children?: React.ReactNode;
  sectionId: string;
  activeSectionId: string;
}

function SectionPanel(props: SectionPanelProps) {
  const { children, sectionId, activeSectionId, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={activeSectionId !== sectionId}
      id={`admin-section-${sectionId}`}
      aria-labelledby={`admin-section-${sectionId}`}
      {...other}
    >
      {activeSectionId === sectionId && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// No longer need to wrap the dashboard with AdminRoleProvider since it's at the app level
const AdminDashboard: React.FC = () => {
  return <AdminDashboardContent />;
};

// Main admin dashboard content
const AdminDashboardContent: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { userRole, isSuperAdmin, loading } = useAdminRole();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeSectionId, setActiveSectionId] = useState<string>('clubs');
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(!isMobile);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    management: true,
    tools: true,
    misc: true
  });
  const [miscMenuItems, setMiscMenuItems] = useState<typeof MENU_ITEMS['misc']>([]);

  // Check for parameters in URL when component mounts
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gameId = params.get('game');
    const tab = params.get('tab');
    
    // Check if we're in a setup or management route
    const path = location.pathname;
    if (path.includes('/live-game-setup/')) {
      setActiveSectionId('liveGames');
    } else if (path.includes('/live-game-management/')) {
      setActiveSectionId('liveGames');
    } else {
      // Convert tab index to section ID if specified in URL
      if (tab) {
        const tabIndex = parseInt(tab);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 7) {
          const sectionId = TAB_INDEX_TO_SECTION_ID[tabIndex];
          if (sectionId) {
            setActiveSectionId(sectionId);
          }
        }
      }
      
      // Set game ID if specified
      if (gameId) {
        setSelectedGameId(gameId);
        // If tab isn't explicitly set but game is, default to Games section
        if (!tab) {
          setActiveSectionId('games');
        }
      }
    }
  }, [location]);

  // Setup menu items based on user role
  useEffect(() => {
    // Set misc menu items based on superadmin status
    if (isSuperAdmin) {
      setMiscMenuItems(MENU_ITEMS.misc);
    } else {
      // If user selected news or materials but is not superadmin, redirect to clubs
      if (activeSectionId === 'news' || activeSectionId === 'materials') {
        setActiveSectionId('clubs');
      }
      // Empty the misc section for regular admins
      setMiscMenuItems([]);
    }
  }, [isSuperAdmin, activeSectionId]);

  useEffect(() => {
    // Close drawer when on mobile by default
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleSelectGameResults = (gameId: string) => {
    setSelectedGameId(gameId);
    setActiveSectionId('gameResults');
  };

  const handleSelectLeague = (leagueId: string) => {
    setSelectedLeagueId(leagueId);
  };
  
  const goToGamesPage = () => {
    setActiveSectionId('games');
  };

  const handleToggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleToggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const renderSectionList = (sectionKey: keyof typeof MENU_ITEMS, sectionTitle: string) => {
    const items = MENU_ITEMS[sectionKey];
    return (
      <>
        <ListItemButton onClick={() => handleToggleSection(sectionKey)}>
          <ListItemText 
            primary={sectionTitle} 
            primaryTypographyProps={{ 
              variant: 'subtitle1', 
              fontWeight: 'bold',
              color: 'primary'
            }} 
          />
          {expandedSections[sectionKey] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={expandedSections[sectionKey]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {items.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton 
                  selected={activeSectionId === item.id || (activeSectionId === 'gameResults' && item.id === 'games')}
                  onClick={() => setActiveSectionId(item.id)}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  // Find the active component
  const findActiveComponent = () => {
    // Special case for gameResults which is no longer in the menu
    if (activeSectionId === 'gameResults') {
      return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <GameResultsAdmin 
            selectedGameId={selectedGameId} 
            selectedLeagueId={selectedLeagueId}
            onSelectLeague={handleSelectLeague}
            goToGames={goToGamesPage}
          />
        </Box>
      );
    }

    for (const section of Object.values(MENU_ITEMS)) {
      const item = section.find(item => item.id === activeSectionId);
      if (item) {
        // Wrap component in Box with padding for better spacing
        if (item.id === 'games') {
          return (
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <item.component 
                onSelectGameResults={handleSelectGameResults} 
                selectedLeagueId={selectedLeagueId}
                onSelectLeague={handleSelectLeague}
              />
            </Box>
          );
        } else {
          return (
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <item.component />
            </Box>
          );
        }
      }
    }
    return null;
  };

  const renderMiscSection = () => {
    // Only render the Misc section if there are items to show (for superadmins)
    if (miscMenuItems.length === 0) {
      return null;
    }
    
    return (
      <>
        <Divider />
        <ListItemButton onClick={() => handleToggleSection('misc')}>
          <ListItemText 
            primary="Misc" 
            primaryTypographyProps={{ 
              variant: 'subtitle1', 
              fontWeight: 'bold',
              color: 'primary'
            }} 
          />
          {expandedSections['misc'] ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={expandedSections['misc']} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {miscMenuItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton 
                  selected={activeSectionId === item.id || (activeSectionId === 'gameResults' && item.id === 'games')}
                  onClick={() => setActiveSectionId(item.id)}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <List>
        {renderSectionList('management', 'Management')}
        <Divider />
        {renderSectionList('tools', 'Tools')}
        {renderMiscSection()}
      </List>
    </Box>
  );

  // Show loading spinner while fetching role information
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Loading admin dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={isMobile ? handleToggleDrawer : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            marginTop: '70px', // Match navbar height
            height: 'calc(100% - 70px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.12)'
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { xs: '100%', sm: `calc(100% - ${drawerOpen ? DRAWER_WIDTH : 0}px)` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="toggle drawer"
                onClick={handleToggleDrawer}
                edge="start"
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography 
              variant="h4" 
              component="h1" 
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              Admin Dashboard
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {user?.email}
            </Typography>
            {userRole && (
              <Chip
                size="small"
                label={userRole.role}
                color={userRole.role === 'SUPERADMIN' ? 'error' : 'primary'}
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
        </Box>
        
        <Paper 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            overflow: 'hidden'
          }}
        >
          {findActiveComponent()}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;